import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SearchWithHistory } from "@/components/ui/search-with-history";
import { addSearchTerm, UNIT_SEARCH_HISTORY_KEY } from "@/lib/searchHistoryCache";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Plus, Check } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Unit, useUnits } from "@/hooks/useUnits";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface UnitSelectorProps {
  trigger?: React.ReactNode;
  onUnitSelected: (unit: Unit) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function UnitSelector({ trigger, onUnitSelected, isOpen, onOpenChange }: UnitSelectorProps) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);

  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      if (searchInput.trim().length >= 2) {
        addSearchTerm(UNIT_SEARCH_HISTORY_KEY, searchInput);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { units, isLoading } = useUnits(
    undefined,
    debouncedSearch.trim() || undefined,
    undefined, undefined,
    1, 10, undefined, undefined, undefined, undefined, undefined, undefined,
    open && !!debouncedSearch.trim()
  );

  const handleSelectUnit = (unit: Unit) => {
    onUnitSelected(unit);
    setOpen(false);
    setSearchInput("");
  };

  const defaultTrigger = (
    <Button variant="outline" className="w-full">
      <Plus className="h-4 w-4 mr-2" />
      Add HIN to Order
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col bg-background">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Add HIN/Unit
          </DialogTitle>
          <DialogDescription>
            Search and add a HIN or unit to your order. Enter the HIN or serial number below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 pr-2">
          <div className="relative">
            <SearchWithHistory
              placeholder="Search by HIN or Serial Number"
              value={searchInput}
              onChange={setSearchInput}
              historyKey={UNIT_SEARCH_HISTORY_KEY}
              className="pl-10 bg-card border-2 border-border shadow-sm"
              icon={<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4 z-10" />}
            />
          </div>

          {debouncedSearch.trim() && (
            <Card className="flex-1 flex flex-col min-h-0 border-border">
              <CardContent className="flex-1 min-h-0 p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner size="lg" label="Loading units..." />
                  </div>
                ) : units.length > 0 ? (
                  <div className="border-t overflow-auto max-h-[400px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead className="text-xs">HIN</TableHead>
                          <TableHead className="text-xs">Serial #</TableHead>
                          <TableHead className="text-xs">Brand</TableHead>
                          <TableHead className="text-xs">Model</TableHead>
                          <TableHead className="text-xs">Year</TableHead>
                          <TableHead className="w-[100px] text-xs">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {units.map((unit) => (
                          <TableRow key={unit.vin}>
                            <TableCell className="font-medium text-xs">{unit.vin}</TableCell>
                            <TableCell className="text-xs">{unit.serialNumber}</TableCell>
                            <TableCell className="text-xs">{unit.make}</TableCell>
                            <TableCell className="text-xs">{unit.model}</TableCell>
                            <TableCell className="text-xs">{unit.modelYear}</TableCell>
                            <TableCell>
                              <Button size="sm" onClick={() => handleSelectUnit(unit)}>
                                Select
                                <Check className="h-4 w-4 ml-1" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No units found matching your search.
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
